import { useState } from 'react';
import { LeadOutcomePicker } from './LeadOutcomePicker';
import { LEAD_OUTCOME_LABELS, type LeadOutcome } from '../../types/leadOutcome.js';
import type { ConversationSummaryView } from '../../lib/leadBot/smsClassification';

// THE AI SUMMARY OF A TEXT CONVERSATION, shared by Kyck's Lead Bot page, the lead history and the club portal.
// Presentational and read-only apart from the host's `onSave`: it makes no request on render (no effect, no query), and the
// model was called long before, by the classification sweep, never by anything that shows this card.
// ⚠️ The suggestion is PRE-SELECTED in the shared outcome picker and saved only when somebody presses Confirm. Without an
// `onSave` (a host with no way to set the outcome) it is shown as text and nothing is offered.

export interface ConversationSummaryCardProps {
  id: string;
  summary: ConversationSummaryView | null;
  /** The outcome as it stands now. */
  current: LeadOutcome;
  setBy?: 'person' | 'ai' | 'system' | null;
  onSave?: (code: LeadOutcome) => Promise<void>;
}

export function ConversationSummaryCard({ id, summary, current, setBy = null, onSave }: ConversationSummaryCardProps) {
  // After a save the card reads as a person's choice without waiting for the host to refetch.
  const [saved, setSaved] = useState<LeadOutcome | null>(null);
  if (!summary) return null;
  return (
    <div className="space-y-1 rounded border border-border bg-muted/30 p-2 text-sm" data-testid="conversation-summary">
      <p className="text-xs font-medium text-muted-foreground">AI summary of this conversation ({summary.confidence} confidence)</p>
      <p className="text-foreground">{summary.summaryText}</p>
      {summary.nextStep && <p className="text-muted-foreground">Next step: {summary.nextStep}</p>}
      {onSave ? (
        <LeadOutcomePicker
          id={id}
          current={saved ?? current}
          suggested={summary.suggestedOutcome}
          setBy={saved ? 'person' : setBy}
          onSave={async (code) => {
            await onSave(code);
            setSaved(code);
          }}
        />
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="conversation-summary-suggestion">
          Suggested by AI: {LEAD_OUTCOME_LABELS[summary.suggestedOutcome]}
        </p>
      )}
    </div>
  );
}
